import { type FC, useContext, useMemo, useState } from 'react';
import { graphql, useFragment, useSubscription } from 'react-relay';

import { NftItemTile } from 'dibs-nft-item-tile/exports/NftItemTile';
import { useCurrency } from 'dibs-buyer-layout/exports/useCurrency';
import { NftProfileImportCollectionRetry } from '../importCollection/NftProfileImportCollectionRetry';
import { ServerVarsContext } from '../../../../../utils/ServerVarsContext';

import { type NftProfileImportCollectionViewNftTile_viewer$key } from './__generated__/NftProfileImportCollectionViewNftTile_viewer.graphql';
import { type NftProfileImportCollectionViewNftTile_nftItem$key } from './__generated__/NftProfileImportCollectionViewNftTile_nftItem.graphql';

const nftItemFragment = graphql`
    fragment NftProfileImportCollectionViewNftTile_nftItem on NftItem {
        serviceId
        importErrorReason
        metadata {
            ... on NftDigitalArtMetadata {
                name
            }
        }
        mostRecentAuction(excludeCanceled: true) {
            ...NftItemTile_nftAuction
        }
        ...NftProfileImportCollectionRetry_nftItem
        ...NftItemTile_nftItem
    }
`;

const viewerFragment = graphql`
    fragment NftProfileImportCollectionViewNftTile_viewer on Viewer {
        ...NftItemTile_viewer
        ...NftProfileImportCollectionRetry_viewer
    }
`;

const subscription = graphql`
    subscription NftProfileImportCollectionViewNftTileSubscription($id: String!) {
        nftItemDraftUpdate(input: { nftItemId: $id }) {
            nftItemDraft {
                serviceId
                # call refetch on subscription next?
            }
        }
    }
`;

export const NftProfileImportCollectionViewNftTile: FC<{
    nftItem: NftProfileImportCollectionViewNftTile_nftItem$key;
    viewer: NftProfileImportCollectionViewNftTile_viewer$key;
    refetchCollection?: () => void;
}> = ({ viewer: viewerRef, nftItem: nftItemRef, refetchCollection }) => {
    const [isRetrying, setIsRetrying] = useState(false);
    const isMobile = !!useContext(ServerVarsContext).isMobile;
    const { currency } = useCurrency();
    const viewer = useFragment(viewerFragment, viewerRef);
    const nftItem = useFragment(nftItemFragment, nftItemRef);
    const id = nftItem.serviceId;
    useSubscription(useMemo(() => ({ variables: { id }, subscription }), [id]));
    const hasError = nftItem.importErrorReason;

    return (
        <li key={nftItem.serviceId}>
            <NftItemTile
                tileType={hasError ? 'COLLECTOR_TILE_NO_CTA' : 'COLLECTOR_TILE_NO_CREATOR'}
                nftItem={nftItem}
                nftAuction={nftItem.mostRecentAuction}
                key={nftItem.serviceId}
                viewer={viewer}
                currency={currency}
                gaLabel="NFT-Collector"
                isMobile={isMobile}
                showSpinner={isRetrying}
            />
            <NftProfileImportCollectionRetry
                setIsRetrying={setIsRetrying}
                viewer={viewer}
                nftItem={nftItem}
                refetchCollection={refetchCollection}
            />
        </li>
    );
};
